/*
 *  Product Gallery
 *    Scripts to handle all product Gallery events
 *
 *  Version
 *    1.0.0 - 2022/11/18
 */

import { Carousel } from '../../objects/carousel';
import { ProductForm } from "./product-form";
import { EventVariantChanged, EVT_VARIANT_CHANGED } from "./variant-selector";

const SELECTOR_GALLERY = '[data-product-gallery-carousel]';
const SELECTOR_CAROUSEL_PROGRESS = '[data-carousel-progress]';
const SELECTOR_PREVIOUS = '[data-product-gallery-prev]';
const SELECTOR_NEXT = '[data-product-gallery-next]';
const SELECTOR_THUMBNAILS = '[data-product-gallery-thumbnail]';
const SELECTOR_SECTION = '[data-product-form-section]';

const ATTR_SLIDE_NUMBER = 'data-product-gallery-thumbnail';
const ATTR_MEDIA_ID = 'data-product-gallery-id';

const CLASS_ACTIVE = 'is-active';

export class ProductGallery {
  container: HTMLElement;
  private formInstance:ProductForm;
  private carousel: Carousel | null = null;
  private carouselContainer: HTMLDivElement | null = null;
  private carouselProgress: HTMLDivElement | null = null;
  private carouselThumbnails: NodeListOf<HTMLButtonElement> | null = null;
  private carouselPreviousBtns: NodeListOf<HTMLButtonElement> | null = null;
  private carouselNextBtns: NodeListOf<HTMLButtonElement> | null = null;
  private template: HTMLElement;
  private section: HTMLElement | null = null;

  constructor(container:HTMLElement, formInstance:ProductForm) {
    this.container = container;
    this.formInstance = formInstance;
    this.template = this.formInstance.container;
    this.queryChildren(container);
    this.attachEvents();
  }

  queryChildren(container: HTMLElement) {
    this.section = container.closest(SELECTOR_SECTION);
    this.carouselContainer = container.querySelector(SELECTOR_GALLERY);
    this.carouselProgress = container.querySelector(SELECTOR_CAROUSEL_PROGRESS);
    this.carouselThumbnails = container.querySelectorAll(SELECTOR_THUMBNAILS);
    this.carouselPreviousBtns = this.section ? this.section.querySelectorAll(SELECTOR_PREVIOUS) : document.querySelectorAll(SELECTOR_PREVIOUS);
    this.carouselNextBtns = this.section ? this.section.querySelectorAll(SELECTOR_NEXT) : document.querySelectorAll(SELECTOR_NEXT);
  }

  attachEvents() {
    if (this.carouselContainer) {
      this.carousel = new Carousel(this.carouselContainer, {
        loop: true,
        autoplay: true,
        slidesPerView: 1,
        onSlideChange: this.setActiveThumbnail.bind(this)
      });

      //Custom slider controls
      this.carouselPreviousBtns?.forEach(btn =>{
        btn.addEventListener('click', ()=>{
          this.carousel?.slider?.prev();
        })
      })

      this.carouselNextBtns?.forEach(btn =>{
        btn.addEventListener('click', ()=>{
          this.carousel?.slider?.next();
        })
      })

      this.carouselThumbnails?.forEach(btn =>{
        btn.addEventListener('click', (e)=>{
          const self = e.target as HTMLElement;
          const slideNumber = self.getAttribute(ATTR_SLIDE_NUMBER) || "1";
          this.carousel?.slider?.moveToSlide(parseInt(slideNumber) - 1);
        })
      })

      this.template.addEventListener(EVT_VARIANT_CHANGED, this.onVariantChanged.bind(this));
    }

    if (!this.carousel) return;
    if (this.carouselProgress) {
      this.carousel.addProgress(this.carouselProgress);
    }
  }

  setActiveThumbnail(slider: Carousel, slideNumber: number) {
    if (this.carouselThumbnails) {
      this.carouselThumbnails.forEach(thumbnail =>{
        thumbnail.classList.remove(CLASS_ACTIVE);
      })
  
      this.carouselThumbnails[slideNumber]?.classList.add(CLASS_ACTIVE);
    }
  }
  
  setActiveSlide(featuredMediaId: number) {
    const slideNumber = this.container.querySelector(`[${ATTR_MEDIA_ID}="${featuredMediaId}"]`);
    if (slideNumber) {
      const slidePosition = slideNumber.getAttribute(ATTR_SLIDE_NUMBER);

      if (slidePosition) {
        this.carousel?.slider?.moveToSlide(parseInt(slidePosition) - 1);
      }
    }
  }

  onVariantChanged(e:Event) {
    const customEvent = e as EventVariantChanged;
    const variant = customEvent?.detail?.newVariant;
    const featuredMediaId = variant.featured_media?.id;

    if (featuredMediaId) {
      this.setActiveSlide(featuredMediaId);
    }
  }

  detatchEvents() {
    this.carousel?.dispose();
    this.template.removeEventListener(EVT_VARIANT_CHANGED, this.onVariantChanged.bind(this));
  }
}